/**
 * 표시 형식 (계획서 §13.1).
 *
 * - 표시 시간대는 `Asia/Seoul` 이다 (§5.1).
 * - 수치는 backend 가 문자열로 준다. **숫자로 파싱해 다시 계산하지 않는다** (§8.6).
 *   화면은 받은 값을 그대로 보여 준다.
 * - 상태는 **색상만으로 구분하지 않는다.** icon 과 text 를 함께 쓴다 (§13.1).
 */

import type {
  AgentStatus,
  ApprovalGrade,
  DryerMode,
  EquipmentId,
  ProductionState,
  RunState,
} from "@/types/api";

const TIME_FORMAT = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** `12:16:08` 형태. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return TIME_FORMAT.format(new Date(iso));
}

/** `01/15 12:16:08` 형태. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return DATE_TIME_FORMAT.format(new Date(iso));
}

/** 초를 `1시간 16분` 형태로. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${String(seconds)}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}분`;
  const hours = Math.floor(minutes / 60);
  return `${String(hours)}시간 ${String(minutes % 60)}분`;
}

/** 천 단위 구분. 계산이 아니라 표시만 바꾼다. */
export function formatCount(value: number): string {
  return value.toLocaleString("ko-KR");
}

/** backend 가 준 수치 문자열에 단위를 붙인다. 값 자체는 건드리지 않는다. */
export function withUnit(value: string | null | undefined, unit: string): string {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}${unit}`;
}

/** 원화 표시. backend 가 이미 원 단위로 반올림한 문자열을 준다 (§5.6). */
export function formatWon(value: string | null | undefined): string {
  if (!value) return "—";
  const [whole] = value.split(".");
  return `${Number(whole).toLocaleString("ko-KR")}원`;
}

export interface StatusBadge {
  icon: string;
  label: string;
  tone: "ok" | "warn" | "error" | "idle";
}

/** 생산설비 상태 (§13.2). */
export function productionBadge(state: ProductionState): StatusBadge {
  switch (state) {
    case "RUNNING":
      return { icon: "▶", label: "가동 중", tone: "ok" };
    case "FAULT_STOPPED":
      return { icon: "✕", label: "고장 정지", tone: "error" };
    case "BREAK_STOPPED":
      return { icon: "⏸", label: "휴게 정지", tone: "warn" };
    default:
      return { icon: "■", label: "정지", tone: "idle" };
  }
}

/** 건조기 mode (§5.3). */
export function dryerBadge(mode: DryerMode): StatusBadge {
  switch (mode) {
    case "PREHEAT":
      return { icon: "🔥", label: "예열", tone: "warn" };
    case "STABILIZE":
      return { icon: "≈", label: "안정화", tone: "warn" };
    case "HOLD":
      return { icon: "⏸", label: "대기(HOLD)", tone: "warn" };
    case "DRYING":
      return { icon: "▶", label: "건조", tone: "ok" };
    case "COOLDOWN":
      return { icon: "❄", label: "냉각", tone: "ok" };
    case "COMPLETE":
      return { icon: "✔", label: "완료", tone: "ok" };
    default:
      return { icon: "■", label: "정지", tone: "idle" };
  }
}

export function runningBadge(running: boolean): StatusBadge {
  return running
    ? { icon: "▶", label: "가동 중", tone: "ok" }
    : { icon: "■", label: "정지", tone: "idle" };
}

const RUN_STATE_LABELS: Record<RunState, string> = {
  IDLE: "대기",
  RUNNING: "정상 운전",
  INCIDENT_ACTIVE: "고장 발생",
  PLANNING: "계획 수립 중",
  WAIT_PLAN_APPROVAL: "계획 승인 대기",
  ADVANCING_TO_START: "기동시각까지 진행",
  WAIT_HUMAN_START: "인간 가동 대기",
  WAIT_PLC_ACK: "PLC 응답 대기",
  EXECUTING: "계획 실행 중",
  WAIT_RISK_CONFIRMATION: "위험 확인 대기",
  PLAN_GENERATION_FAILED: "계획 생성 실패",
  COMPLETE: "완료",
};

export function runStateLabel(state: RunState): string {
  return RUN_STATE_LABELS[state];
}

const AGENT_STATUS_LABELS: Record<AgentStatus, StatusBadge> = {
  IDLE: { icon: "○", label: "대기", tone: "idle" },
  RUNNING: { icon: "◌", label: "실행 중", tone: "warn" },
  WAITING: { icon: "⏸", label: "보류", tone: "warn" },
  COMPLETE: { icon: "✔", label: "완료", tone: "ok" },
  ERROR: { icon: "✕", label: "실패", tone: "error" },
};

export function agentBadge(status: AgentStatus): StatusBadge {
  return AGENT_STATUS_LABELS[status];
}

const GRADE_LABELS: Record<ApprovalGrade, string> = {
  AUTO_APPROVE: "자동승인·통보",
  TIMED_AUTO_APPROVE: "10분 무응답 시 자동승인",
  EXPLICIT_APPROVAL: "명시적 승인 필요",
};

export function approvalGradeLabel(grade: ApprovalGrade): string {
  return GRADE_LABELS[grade];
}

const EQUIPMENT_LABELS: Record<EquipmentId, string> = {
  WELDER: "용접기",
  EPOXY: "에폭시 주입기",
  DRYER: "건조기",
  DUST_COLLECTOR: "집진기",
  COMPRESSOR: "컴프레서",
};

export function equipmentLabel(id: EquipmentId): string {
  return EQUIPMENT_LABELS[id];
}

/** `datetime-local` input 이 쓰는 `YYYY-MM-DDTHH:mm:ss` (Asia/Seoul 기준). */
export function toLocalInputValue(iso: string): string {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(iso));
  return parts.replace(" ", "T");
}

/** `datetime-local` 값을 Asia/Seoul 기준 ISO 로 되돌린다. */
export function fromLocalInputValue(value: string): string {
  return `${value.length === 16 ? `${value}:00` : value}+09:00`;
}
